/**
 * Debug Utilities
 *
 * Helper functions for debugging and development mode detection
 */

import { Capacitor } from '@capacitor/core';

/**
 * Check if the application is running in debug mode
 * @returns {boolean} True if debug mode is enabled
 */
export const isDebugMode = () => {
    return process.env.REACT_APP_DEBUG_MODE === 'true';
};

/**
 * Check if debug deck mode is enabled (for using prepared test decks)
 * @returns {boolean} True if debug deck mode is enabled
 */
export const isDebugDeck = () => {
    return process.env.REACT_APP_DEBUG_DECK === 'true';
};

/**
 * Check if logging should be enabled
 * - In mobile (Capacitor): Always log (for Sentry and debugging)
 * - In web: Only log if REACT_APP_DEBUG_MODE is true
 * @returns {boolean} True if logging should be enabled
 */
export const shouldLog = () => {
    // En plataformas nativas (iOS/Android), siempre hacer log
    if (Capacitor.isNativePlatform()) {
        return true;
    }

    // En web, solo si el modo debug está activado
    return isDebugMode();
};

/**
 * Console log that only outputs when appropriate:
 * - Mobile (Capacitor): Always (for Sentry and debugging)
 * - Web: Only if REACT_APP_DEBUG_MODE is true
 * @param {...any} args - Arguments to pass to console.log
 */
export const debugLog = (...args) => {
    if (shouldLog()) {
        console.log(...args);
    }
};

/**
 * Console warn that only outputs when appropriate:
 * - Mobile (Capacitor): Always (for Sentry and debugging)
 * - Web: Only if REACT_APP_DEBUG_MODE is true
 * @param {...any} args - Arguments to pass to console.warn
 */
export const debugWarn = (...args) => {
    if (shouldLog()) {
        console.warn(...args);
    }
};

/**
 * Console error that only outputs when appropriate:
 * - Mobile (Capacitor): Always (for Sentry and debugging)
 * - Web: Only if REACT_APP_DEBUG_MODE is true
 * @param {...any} args - Arguments to pass to console.error
 */
export const debugError = (...args) => {
    if (shouldLog()) {
        console.error(...args);
    }
};

/**
 * Console table that only outputs when appropriate:
 * - Mobile (Capacitor): Always (for Sentry and debugging)
 * - Web: Only if REACT_APP_DEBUG_MODE is true
 * @param {...any} args - Arguments to pass to console.table
 */
export const debugTable = (...args) => {
    if (shouldLog()) {
        console.table(...args);
    }
};

/**
 * Console group that only outputs when appropriate:
 * - Mobile (Capacitor): Always (for Sentry and debugging)
 * - Web: Only if REACT_APP_DEBUG_MODE is true
 * @param {...any} args - Arguments to pass to console.group
 */
export const debugGroup = (...args) => {
    if (shouldLog()) {
        console.group(...args);
    }
};

/**
 * Console groupEnd that only outputs when appropriate:
 * - Mobile (Capacitor): Always (for Sentry and debugging)
 * - Web: Only if REACT_APP_DEBUG_MODE is true
 */
export const debugGroupEnd = () => {
    if (shouldLog()) {
        console.groupEnd();
    }
};
